import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Award, ChevronUp, LifeBuoy, LogOut, UserSquare2 } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthContext';
import { useLanguage } from '@/hooks/useLanguage';
import { cn, initials } from '@/lib/utils';
import { ADMIN_GROUPS, groupsFor } from './navigation';
import { LanguageSwitcher } from './LanguageSwitcher';
import { ThemeToggle } from './ThemeToggle';

/**
 * Menu du compte, deroule vers le haut depuis le pied de la barre laterale.
 *
 * Il rassemble ce qui ne releve pas du parcours lui-meme : l'administration
 * de la plateforme pour qui y a droit, puis le profil, les certificats,
 * l'aide, la langue et le theme. La barre laterale reste ainsi consacree a
 * la formation de l'agent.
 *
 * Account menu, opening upwards from the foot of the sidebar: platform
 * administration for those entitled to it, then everything belonging to the
 * person. The sidebar itself stays devoted to the training path.
 */

const LINKS = [
  { to: '/profile', labelKey: 'nav.profile', icon: UserSquare2 },
  { to: '/certificates', labelKey: 'nav.certificates', icon: Award },
  { to: '/help', labelKey: 'nav.help', icon: LifeBuoy },
] as const;

export function UserMenu() {
  const { user, signOut, isStaff } = useAuth();

  // Le menu ne montre que les ecrans que ce role peut reellement ouvrir.
  const adminGroups = groupsFor(ADMIN_GROUPS, user?.role);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);

  // Le menu se referme a la navigation, comme le menu mobile.
  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      // Le clavier ne doit pas perdre le fil : le declencheur reprend le focus.
      trigger.current?.focus();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleSignOut = async () => {
    setOpen(false);
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div ref={container} className="relative">
      {open ? (
        <div
          role="menu"
          aria-label={t('nav.account')}
          className="absolute bottom-full left-0 right-0 z-10 mb-2 max-h-[70vh] overflow-y-auto overscroll-contain rounded-2xl border border-border bg-surface shadow-lg shadow-ink/10"
        >
          {/* Administration : regroupee par nature de tache, comme la barre
              laterale l'est pour la formation. */}
          {isStaff
            ? adminGroups.map((group) => (
                <div key={group.titleKey} className="border-b border-border p-1.5">
                  <p className="px-3 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-subtle">
                    {t(group.titleKey)}
                  </p>
                  <ul>
                    {group.items.map(({ to, labelKey, icon: Icon, end }) => (
                      <li key={to}>
                        <NavLink
                          to={to}
                          end={end}
                          role="menuitem"
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-primary-soft text-primary'
                                : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
                            )
                          }
                        >
                          <Icon className="h-4.5 w-4.5 shrink-0" aria-hidden />
                          <span className="truncate">{t(labelKey)}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            : null}

          <ul className="p-1.5">
            {LINKS.map(({ to, labelKey, icon: Icon }) => (
              <li key={to}>
                <Link
                  to={to}
                  role="menuitem"
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
                >
                  <Icon className="h-4.5 w-4.5 shrink-0" aria-hidden />
                  <span className="truncate">{t(labelKey)}</span>
                </Link>
              </li>
            ))}
          </ul>

          {/* Preferences d'affichage : elles restent visibles pendant qu'on
              les change, le menu ne se referme donc pas ici. */}
          <div className="space-y-2.5 border-t border-border px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-medium text-ink-muted">{t('common.language')}</span>
              <LanguageSwitcher compact />
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-medium text-ink-muted">{t('common.theme')}</span>
              <ThemeToggle />
            </div>
          </div>

          <div className="border-t border-border p-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={() => void handleSignOut()}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-ink-muted transition-colors hover:bg-danger-soft hover:text-danger"
            >
              <LogOut className="h-4.5 w-4.5 shrink-0" aria-hidden />
              {t('nav.logout')}
            </button>
          </div>
        </div>
      ) : null}

      <button
        ref={trigger}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-colors',
          open ? 'bg-surface-muted' : 'bg-surface-muted/60 hover:bg-surface-muted',
        )}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-ink">
          {initials(user?.firstName, user?.lastName)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-ink">
            {user?.firstName} {user?.lastName}
          </span>
          <span className="block truncate text-xs text-ink-muted">{user ? t(`roles.${user.role}`) : ''}</span>
        </span>
        <ChevronUp
          className={cn('h-4 w-4 shrink-0 text-ink-subtle transition-transform', !open && 'rotate-180')}
          aria-hidden
        />
      </button>
    </div>
  );
}
